/**
 * NIP jako TOŻSAMOŚĆ OPERATORA, nie jako dana do faktury.
 *
 * Do tej pory NIP był polem w formularzu rejestracji i nic od niego nie
 * zależało. Od chwili, w której każdy operator dostaje własny realm tożsamości,
 * rozstrzyga rzecz nieodwracalną: czy firma zgłaszająca się po subdomenę, na
 * którą realm już istnieje, jest **tą samą firmą** — czy inną, której trzeba
 * odmówić. Porównanie musi więc być odporne na zapis (`PL 123-456-32-18`
 * i `pl1234563218` to jeden podatnik), a literówka musi odpaść PRZED zapisem:
 * zły numer tworzy „innego podatnika", który potem zablokuje właściwego.
 *
 * `isValidNip` z `validation.ts` odpowiada na inne pytanie — czy pole
 * formularza jest poprawne. Tutaj chodzi o porównywanie tożsamości, dlatego
 * dochodzi postać kanoniczna i obsługa prefiksu kraju.
 */
/**
 * Postać kanoniczna: bez spacji, myślników i kropek, wielkimi literami.
 *
 * Jedyna postać, w której NIP wolno porównywać i zapisywać. Zapisanie postaci
 * wpisanej przez człowieka znaczyłoby, że ta sama firma raz jest sobą, a raz
 * nie — zależnie od tego, czy ktoś wstawił myślniki.
 */
export function normalizeTaxId(raw) {
    return raw.replace(/[^0-9A-Za-z]/g, "").toUpperCase();
}
/**
 * Rozbierz numer na prefiks kraju i cyfry.
 *
 * Prefiks jest opcjonalny: krajowe `1234563218` i unijne `PL1234563218` dotyczą
 * tego samego podatnika.
 */
export function parseTaxId(raw) {
    const s = normalizeTaxId(raw);
    const zPrefiksem = /^([A-Z]{2})([0-9A-Z]+)$/.exec(s);
    if (zPrefiksem)
        return { country: zPrefiksem[1], digits: zPrefiksem[2] };
    return { country: null, digits: s };
}
const WAGI_PL = [6, 5, 7, 2, 3, 4, 5, 6, 7];
/**
 * Czy to poprawny polski NIP: dziesięć cyfr i zgodna suma kontrolna.
 *
 * Reszta 10 jest niemożliwa do zapisania w jednej cyfrze, więc taki numer nie
 * istnieje — odrzucamy go zamiast brać `% 10` i uznawać za poprawny.
 */
export function isValidPolishTaxId(digits) {
    if (!/^[0-9]{10}$/.test(digits))
        return false;
    // Same cyfry powtórzone przechodzą sumę kontrolną dla kilku wartości
    // (`0000000000` wśród nich) i nigdy nie są prawdziwym numerem. Ten strażnik
    // mają 33rp i usługa tenantów; b00k go nie miał, więc `0000000000` był tutaj
    // — i TYLKO tutaj — poprawnym kluczem tenanta.
    if (/^(\d)\1{9}$/.test(digits))
        return false;
    const suma = WAGI_PL.reduce((a, w, i) => a + w * Number(digits[i]), 0);
    const reszta = suma % 11;
    return reszta !== 10 && reszta === Number(digits[9]);
}
/**
 * Sprawdź numer podany przez użytkownika.
 *
 * Sumę kontrolną liczymy WYŁĄCZNIE dla Polski i to jest świadome ograniczenie:
 * każdy kraj ma własny algorytm, a udawanie, że sprawdzamy niemiecki
 * `USt-IdNr.`, byłoby gorsze niż jawne przepuszczenie go po samym formacie.
 * Dla numerów spoza Polski literówka NIE zostanie tu złapana.
 */
export function checkTaxId(raw) {
    const s = normalizeTaxId(raw);
    if (s === "")
        return "empty";
    const { country, digits } = parseTaxId(s);
    if (country === null || country === "PL") {
        if (!/^[0-9]{10}$/.test(digits))
            return "invalid_format";
        return isValidPolishTaxId(digits) ? null : "invalid_checksum";
    }
    return /^[0-9A-Z]{2,13}$/.test(digits) ? null : "invalid_format";
}
/**
 * Klucz, po którym rozstrzygamy „to ta sama firma".
 *
 * Prefiks `PL` jest doklejany zawsze, bo `PL1234563218` i `1234563218` to jeden
 * podatnik — bez tego ta sama firma, która raz wpisała NIP z prefiksem, a raz
 * bez, wyglądałaby na dwie i sama sobie zablokowałaby subdomenę.
 */
export function tenantKeyFromTaxId(raw) {
    const { country, digits } = parseTaxId(raw);
    return country === null || country === "PL" ? `PL${digits}` : `${country}${digits}`;
}
/**
 * Czy oba numery wskazują tego samego podatnika.
 *
 * Pusty numer nie jest równy niczemu — także drugiemu pustemu. Realm bez
 * zapisanego NIP-u nie może zostać przejęty przez nikogo: milczenie atrybutu
 * nie jest zgodą.
 */
export function sameTaxpayer(a, b) {
    return tenantKeyFromTaxId(a) === tenantKeyFromTaxId(b) && normalizeTaxId(a) !== "";
}
//# sourceMappingURL=tax-id.js.map